/* eslint-disable @typescript-eslint/no-unused-vars */
import { api } from './api';
import * as actionTypes from './action-types';
import { ValueToStoreData, ApiToUse, SimpleFormSerialize, SimpleFormState } from './types';
import { FIELD_TYPE } from './field-types';
import { mapToOptionsRequestParams } from '../data/codes';
import { createDescriptionSelector } from 'app/infrastructure/translate';
import {
  validate, convertToValidationRule,
  SimpleFormValidationConfig, SimpleFormValidationProp, SimpleFormValidationRule
} from 'app/infrastructure/simple-form/validation';
import { ErrorNotification } from 'app/infrastructure/notifications/user-messages';
import { ActionDispatching, TextCollection } from 'app/types-shared';
import { errorSafe, LoggedError } from 'app/infrastructure/redux';
import get from 'lodash/get';
import { OptionsRequestParams } from 'app/infrastructure/ui/form/types';

// eslint:disable-next-line:max-func-body-length
export const actionCreators = (path: string | string[], texts?: TextCollection) => ({

  /**
   * Components defining a onChange event gets an onChange callback assigned.
   * onChange calls into this action creator whenever the components value is changed.
   * The redux store will be updated with the new value;
   */
  valueChanged: (
    componentContext: string,
    newValue: any): ActionDispatching<any, any> => errorSafe(async (dispatch: any, getState: any) => {

      dispatch(new actionTypes.ValueChange(path, componentContext, newValue));
    }),

  /**
   * Components defining an onLoadOptions event gets a callback assigned.
   * The callback assigned calls into this action creator to load code table options data into the redux store.
   * The related reducer will store the data using the componentContext as id in a temporary/options store slice.
   */
  loadOptions: (
    componentContext: string,
    componentEndpoint: string,
    loadOptionsParams: any,
    optionsParams?: any,
    apiToUse?: ApiToUse): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new actionTypes.RequestCodetable(path, componentContext));

      try {
        const response: any = await api.getOptions(
          loadOptionsParams && loadOptionsParams.query
            ? `${componentEndpoint}/search`
            : componentEndpoint,
          mapToOptionsRequestParams(loadOptionsParams),
          optionsParams,
          apiToUse);
        const complexResponseWithFilteredOutZero = response?.data?.rootCodes;
        const simpleResponse = response?.data ?? null;
        const value = response?.data?.v;
        dispatch(new actionTypes.ReceiveCodetable(path, componentContext, loadOptionsParams?.query,
          complexResponseWithFilteredOutZero || simpleResponse, value));
      } catch (err) {
        dispatch(new LoggedError(err));
        dispatch(new actionTypes.FailCodetable(path, componentContext));
      }
    },

  /**
   * Components defining an onLoadCategoryOptions event gets a callback assigned.
   * The callback assigned calls into this action creator to load code table categoryOptions data into the redux store.
   * The related reducer will store the data using the componentContext as id in a temporary/categoryOptions store slice.
   */
  loadCategoryOptions: (
    componentContext: string,
    componentEndpoint: string,
    loadCategoryOptionsParams: OptionsRequestParams,
    optionsParams?: any,
    apiToUse?: ApiToUse): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new actionTypes.RequestCodetableCategory(path, componentContext));

      try {
        const response: any = await api.getOptions(
          loadCategoryOptionsParams?.query
            ? `${componentEndpoint}/search`
            : componentEndpoint,
          mapToOptionsRequestParams(loadCategoryOptionsParams),
          optionsParams,
          apiToUse);
        const complexResponseWithFilteredOutZero = response?.data?.rootCodes;
        const simpleResponse = response?.data ?? null;
        const value = response?.data?.v;
        dispatch(new actionTypes.ReceiveCodetableCategory(path, componentContext, loadCategoryOptionsParams?.query,
          complexResponseWithFilteredOutZero || simpleResponse, value));
      } catch (err) {
        dispatch(new LoggedError(err));
        dispatch(new actionTypes.FailCodetableCategory(path, componentContext));
      }
    },

  /**
   * Component can add certain values (ex. retrieved from current user data) and save it
   * as form-compatible initial value 
   */
  insertValueToStore: (
    input: ValueToStoreData
  ): ActionDispatching<any, any> => errorSafe(async (dispatch: any, getState: any) => {

    dispatch(new actionTypes.InsertValueToStore(path, input));
  }),

  insertComponentDataToStore: (name: string, type: FIELD_TYPE,
    serialize: SimpleFormSerialize,
    validationProps: SimpleFormValidationProp[]): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new actionTypes.InsertComponentDataToStore(path, name, type, serialize, validationProps));
    }),

  clearValueInStore: (name): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new actionTypes.ClearValueInStore(path, name));
    }),

  clearAllValues: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new actionTypes.ClearAllValues(path));
    }),

  resetChangesListener: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new actionTypes.ResetChangesListener(path));
    }),

  validate: (configurationObject: SimpleFormValidationConfig,
    onContinue?: (any: any) => void,
    errorMessage?: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      const state = get(getState(), path) as SimpleFormState;
      const { simpleForm: {
        editing: { values = {} } = {}, componentData = {}
      } = {} } = state || {};

      const validationRules = Object.keys(componentData)
        .reduce((memo, key) => {
          if (componentData[key].validationProps) {
            return [
              ...memo,
              ...componentData[key].validationProps
                .map(validationProp => convertToValidationRule(key, validationProp))
            ];
          }
          return memo;
        }, [] as SimpleFormValidationRule[]);

      const translate = createDescriptionSelector(texts ?? state?.texts);
      const validation = validate([...configurationObject, ...validationRules], values, translate);

      dispatch(new actionTypes.ValidationChange(path, validation));

      if (!!Object.keys(validation).length) {

        // validation errors - do not send data
        if (errorMessage) {
          dispatch(new ErrorNotification(errorMessage));
        }
        return;
      }

      if (onContinue) {
        dispatch(onContinue);
      }
    },

  validationClear: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new actionTypes.ValidationChange(path, []));
    })

});
